// One-shot lifelink-on-strike grant (Gift of the Raven). Pure. No
// React, no broadcasts. The runtime installs the built entry on the
// picked minion's `_transientAutomation[]`; the combat-trigger path
// (`_fireCombatTrigger`) walks transient on_damage_dealt entries on
// the striker alongside its intrinsic automation, resolves the heal,
// and consumes the one-shot.
//
// ── Sorcery rule (Gift of the Raven) ──
// "Choose an allied minion. The next time they strike a unit this
// turn, the damage heals you." A single strike that deals damage to a
// UNIT (minion or avatar, not a site) heals the minion's controller by
// the damage dealt, then the grant is gone. Only the next strike, and
// only this turn.

import { TRIGGER, OP } from '../schema';

/**
 * Build the transient lifelink entry installed on the picked minion.
 * `expiresAtTurn` / `expiresForPlayer` reuse the same prune lifecycle
 * as Enduring Faith (turnPhaseRunner end-phase sweep), so a grant that
 * never triggers is cleaned up at the end of the controller's turn.
 * `_oneShot` marks the entry for consumption after a single fire.
 * `watch_target_filter` with `not_site` gates to units (codex: "strike
 * a unit"), so a site / rubble strike never consumes or heals.
 */
export function buildStrikeLifelinkEntry(controller, expiresAtTurn, sourceLabel) {
  return {
    trigger: TRIGGER.ON_DAMAGE_DEALT,
    cost: [],
    condition: { type: 'watch_target_filter', not_site: true },
    scope: { where: 'self' },
    effect: [{ op: OP.HEAL_PLAYER, args: { from_watch_amount: true } }],
    duration: 'this_turn',
    expiresAtTurn,
    expiresForPlayer: controller,
    sourceLabel,
    _oneShot: true,
  };
}

/**
 * Walk a striker's transient automation for entries matching the fired
 * combat trigger. Returns the matching entries (callers fire them, then
 * call `consumeOneShotTransients` to drop the consumed grants). Empty
 * array when the card carries no transient entries.
 */
export function transientCombatEntries(card, triggerType) {
  const list = card?._transientAutomation;
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.filter((e) => e.trigger === triggerType);
}

/**
 * Remove the one-shot transient entries that just fired from the
 * card's `_transientAutomation[]`. Non-one-shot entries (e.g. Enduring
 * Faith's redirect aura) are left in place. Mutates in place.
 */
export function consumeOneShotTransients(card, firedEntries) {
  if (!card?._transientAutomation?.length || !firedEntries?.length) return;
  const fired = new Set(firedEntries.filter((e) => e._oneShot));
  if (fired.size === 0) return;
  card._transientAutomation = card._transientAutomation.filter((e) => !fired.has(e));
}
